import {
  filterTimetable,
  formatStartAndEndTime,
  getCurrentTimetableRules,
  getTimezonedDate,
  getValidTimetableRulesByDay,
  isDateAllowedOnTimetable,
  TimetableRule,
  TimetableRuleState,
  TimetableRuleType,
} from '@chirp/access-verification';

export { isDateAllowedOnTimetable };

export function getAvailabilityDescription(
  date = new Date(),
    timetable: TimetableRule[] | null | undefined,
    timezone: string,
) {
  const timezonedDate = getTimezonedDate(date, timezone);
  const filteredTimetable = filterTimetable(timezonedDate, timetable, timezone);

  if (!filteredTimetable) {
    return '';
  }

  const currentTimetableRules = getCurrentTimetableRules(timezonedDate, filteredTimetable);

  if (currentTimetableRules.some(rule => rule.type === TimetableRuleType.RESTRICTION)) {
    return '';
  }

  const validTimetableRules = getValidTimetableRulesByDay(timezonedDate, filteredTimetable);

  if (validTimetableRules.length > 0) {
    const [timetableRule] = validTimetableRules;

    if (timetableRule.start === '00:00' && timetableRule.end === '00:00') {
      return '';
    }

    const { startTime, endTime } = formatStartAndEndTime(timezonedDate, timetableRule);

    return `${startTime.format('h:mm A')} - ${endTime.format('h:mm A')}`;
  }

  return '';
}

export function getAvailabilityState(
  date = new Date(),
  timetable: TimetableRule[] | null | undefined,
  timezone: string,
) {
  const timezonedDate = getTimezonedDate(date, timezone);
  const filteredTimetable = filterTimetable(timezonedDate, timetable, timezone);

  if (!filteredTimetable) {
    return TimetableRuleState.CONTROLLED;
  }

  const currentTimetableRules = getCurrentTimetableRules(timezonedDate, filteredTimetable);

  if (currentTimetableRules.some(r => r.state === TimetableRuleState.LOCKDOWN)) {
    return TimetableRuleState.LOCKDOWN; // LOCKDOWN has highest priority
  }

  if (currentTimetableRules.some(r => r.state === TimetableRuleState.UNLOCKED)) {
    return TimetableRuleState.UNLOCKED; // UNLOCKED has second highest priority
  }

  const state = currentTimetableRules[0] ? currentTimetableRules[0].state : null;

  return state || TimetableRuleState.CONTROLLED;
}

export function getLockdownEndDateString(
  date = new Date(),
  timetable: TimetableRule[] | null | undefined,
  timezone: string,
): string {
  const timezonedDate = getTimezonedDate(date, timezone);
  const filteredTimetable = filterTimetable(timezonedDate, timetable, timezone);

  if (!filteredTimetable) {
    return 'Temporarily unavailable until further notice.';
  }

  const currentTimetableRules = getCurrentTimetableRules(timezonedDate, filteredTimetable);
  const lockdownTimetableRule = currentTimetableRules.find(rule => rule.state === TimetableRuleState.LOCKDOWN);

  if (!lockdownTimetableRule || !lockdownTimetableRule.endDate) {
    return 'Temporarily unavailable until further notice.';
  }

  const timezonedEndDate = getTimezonedDate(new Date(lockdownTimetableRule.endDate), timezone);
  const timezonedEndDateString = timezonedEndDate.format('MMMM Do YYYY, h:mm a');

  return `Temporarily unavailable until ${timezonedEndDateString}.`;
}

function getDaysStringFromTimetable(ttRule: TimetableRule) {
  // EX. [1,2,4,5,6,7]
  const days = ttRule.days;

  // Group consecutive days together in an array
  // IN:  [1,2,4,5,6,7]
  // OUT: [[1,2],[4,5,6,7]]
  let daysGrouped = days.reduce<(number[])[]>((prev, curr) => {
    const newArr = [...prev];
    const targetArr = newArr.findIndex((val) => val.includes(curr - 1));
    if (targetArr !== -1) {
      newArr[targetArr].push(curr);
    } else {
      newArr.push([curr]);
    }
    return newArr;
  }, []);

  // Filter out numbers in the middle
  // IN:  [[1,2],[4,5,6,7]]
  // OUT: [[1,2],[4,7]]
  daysGrouped = daysGrouped.map((dayGroup) => {
    if (dayGroup.length < 3) return dayGroup;
     return [dayGroup[0], dayGroup[dayGroup.length - 1]];
  });

  // Map numbers to day abbreviation
  // Filter out numbers in the middle
  // IN:  [[1,2],[4,7]]
  // OUT: [['Mo','Tu'],['Th','Su']]
  daysGrouped = daysGrouped.map((dayGrouped) => {
    const dayMap = {
      1: 'Mo',
      2: 'Tu',
      3: 'We',
      4: 'Th',
      5: 'Fr',
      6: 'Sa',
      7: 'Su',
    };

    return dayGrouped.map((day) => {
      if ([1, 2, 3, 4, 5, 6, 7].includes(day)) {
        // @ts-ignore - day is guranteed to be 1-7 so it can be used to index dayMap
        return dayMap[day];
      }
      return '';
    });
  });

  // Maps into display string groups
  // IN:  [['Mo','Tu'],['Th','Su']]
  // OUT: [['Mo-Tu'], ['Th-Su']]
  const daysGroupedString = daysGrouped.map((dayGrouped) => {
    if (dayGrouped.length < 2) {
      return `${dayGrouped[0]}`;
    } 
      return `${dayGrouped[0]}-${dayGrouped[1]}`;
    
  });

  // Maps into single display string
  // IN:  [['Mo-Tu'], ['Th-Su']]
  // OUT: 'Mo-Tu, Th-Su'
  let daysString = '';
  daysGroupedString.forEach((dayString, i) => {
    if (i === 0) {
      daysString = dayString;
    } else {
      daysString = `${daysString}, ${dayString}`;
    }
  });

  return daysString;
}

export function getAvailabilitySchedule(
    date = new Date(),
    timetable: TimetableRule[] | null | undefined,
    timezone: string,
) {
  const timezonedDate = getTimezonedDate(date, timezone);
  const filteredTimetable = filterTimetable(timezonedDate, timetable, timezone);
  const scheduleArr: string[] = [];

  if (filteredTimetable && filteredTimetable.length > 0) {
    filteredTimetable.forEach((ttRule) => {
      const days = getDaysStringFromTimetable(ttRule);

      const { startTime, endTime } = formatStartAndEndTime(timezonedDate, ttRule);

      const startStr = startTime.format('h:mm A');
      const endStr = endTime.format('h:mm A');

      scheduleArr.push(`${startStr} - ${endStr}, ${days}`);
    });
  }

  return scheduleArr;
}

export function getAllAvailabilitySchedule(
  date = new Date(),
  timetable: TimetableRule[] | null | undefined,
  timezone: string,
): Record<TimetableRuleState, string[]> {
  const currentdDate = getTimezonedDate(date, timezone);
  const scheduleDict: Record<TimetableRuleState, string[]> = {
    [TimetableRuleState.CONTROLLED]: [],
    [TimetableRuleState.UNLOCKED]: [],
    [TimetableRuleState.LOCKDOWN]: [],
  };
  let is247 = true;

  if (timetable && timetable.length > 0) {
    timetable.forEach((ttRule) => {
      if (ttRule.state === TimetableRuleState.LOCKDOWN) {
        if (ttRule.startDate && ttRule.endDate) {
          const timezonedStartDate = getTimezonedDate(new Date(ttRule.startDate), timezone);
          const timezonedEndDate = getTimezonedDate(new Date(ttRule.endDate), timezone);
          if (currentdDate.isBetween(timezonedStartDate, timezonedEndDate, 'dates', '[]')) {
            scheduleDict[TimetableRuleState.LOCKDOWN].push(
              `${timezonedStartDate.local().format('MMM DD YYYY, h:mm A')} - ${timezonedEndDate.local().format('MMM DD YYYY, h:mm A')}`,
            );
          }
        }
      } else {
        const days = getDaysStringFromTimetable(ttRule);
        const { startTime, endTime } = formatStartAndEndTime(currentdDate, ttRule);

        const startStr = startTime.format('h:mm A');
        const endStr = endTime.format('h:mm A');

        const state = ttRule.state;

        if (state) {
          scheduleDict[state].push(`${startStr} - ${endStr}, ${days}`);
        }

        if (ttRule.state === TimetableRuleState.CONTROLLED) {
          is247 = false;
        }
      }
    });
  }

  if (is247) {
    scheduleDict[TimetableRuleState.CONTROLLED].push(
      '24/7',
    );
  }

  return scheduleDict;
}
